<template>
    <Layout class="employees-page" is-crm>
        <header class="page__top employees-page__header">
            <h1 v-t="'crm.employees.title'"></h1>
            <div class="employees-page__toolbar">
                <WithGlobalPermission permission="Invite.list">
                    <div>
                        <router-link class="link" :to="invitesRoute">Invites</router-link>
                    </div>
                </WithGlobalPermission>
                <WithGlobalPermission permission="Employee.create_users">
                    <router-link
                        :to="{ name: 'editors-create' }"
                        type="button"
                        class="button button--big employees-page__create-batch"
                    >
                        <IconUser />
                        <span v-text="$t('crm.employees.add.trigger')" />
                    </router-link>
                </WithGlobalPermission>
            </div>
        </header>
        <PaginatedResource
            ref="employeesResource"
            :fetch-method="fetchEmployees"
            :filter-fields="['role']"
            :sorting-fields="['id']"
            filter-key="employees"
            @filterUpdate="onFilterUpdate"
        >
            <template
                #default="{
                    loading,
                    sort,
                    search,
                    filter,
                    canLoadMore,
                    resource: users,
                    isEmpty,
                    notFound,
                    filters,
                    loadMore,
                }"
            >
                <div class="employees">
                    <Spinner v-if="loading" />
                    <UsersFilters
                        class="employees-page__filters"
                        :filters="filters"
                        :roles="roles"
                        @search="search"
                        @filter="filter"
                    />
                    <UsersTable
                        v-if="!isEmpty"
                        class="employees-page__table"
                        :users="users"
                        user-router="editors-id"
                        @sort="sort"
                    >
                        <template #item-actions="{ user }">
                            <UsersEdit
                                v-if="user.permissions.update"
                                :value="user"
                                class="user-action"
                                :schema="schema.partial_update"
                                :languages="languagesArray"
                                modal-title="crm.employees.edit.title"
                                @save="updateUser"
                            />
                            <UsersDelete
                                v-if="user.permissions.destroy"
                                class="user-action"
                                @confirmed="deleteUser(user.id)"
                            />
                        </template>
                    </UsersTable>
                    <div v-if="notFound" class="empty">
                        {{ $t('crm.users.list.notFound') }}
                    </div>
                    <div v-if="isEmpty" class="empty">
                        {{ $t('crm.users.list.empty') }}
                    </div>
                    <div v-if="canLoadMore" class="employees__loadmore">
                        <button
                            class="button button--big button--blank"
                            type="button"
                            @click="loadMore"
                        >
                            {{ $t('misc.loadmore') }}
                        </button>
                    </div>
                </div>
            </template>
        </PaginatedResource>
    </Layout>
</template>

<script>
import {
    fetchEmployees,
    updateEmployee,
    deleteEmployee,
    fetchEmployeesLanguages,
} from '../../api/employees';
import { UsersDelete, UsersEdit, UsersFilters, UsersTable } from '../../../users/components';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { IconUser } from '@speechki-frontend/shared/ui/icons';
import { WithGlobalPermission } from '@speechki-frontend/core/auth/components';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
    name: 'EmployeesPage',
    components: {
        Layout,
        Spinner,
        IconUser,
        PaginatedResource,
        UsersDelete,
        UsersEdit,
        UsersFilters,
        UsersTable,
        WithGlobalPermission,
    },
    data() {
        return {
            languages: {},
        };
    },
    computed: {
        ...mapState('employee', ['schema']),
        ...mapGetters(['isAdmin']),

        roles() {
            if (!this.schema.create) return [];
            const { role } = this.schema.create;
            const { choices = [] } = role.child;
            return choices;
        },
        languagesArray() {
            return Object.entries(this.languages).map(([id, label]) => ({ id, label }));
        },
        invitesRoute() {
            const id = this.workspaceCustomerId || 'speechki';

            return { name: 'customers-id-invites', params: { id } };
        },
    },
    async created() {
        try {
            await Promise.all([this.fetchSchema(), this.fetchLanguages()]);
        } catch (e) {
            console.log(e);
        }
    },
    methods: {
        fetchEmployees,
        ...mapActions(['pushToast']),
        ...mapActions('employee', ['fetchSchema']),

        refetch() {
            this.$refs.employeesResource.fetch();
        },
        onFilterUpdate(diff) {
            console.log('onFilterUpdate', diff);
        },
        async fetchLanguages() {
            this.loading = true;

            try {
                this.languages = await fetchEmployeesLanguages();
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },
        async updateUser({ id, languages, role }) {
            try {
                await updateEmployee(id, { languages, role });
                this.refetch();
                this.pushToast({
                    id: Date.now(),
                    type: 'success',
                    title: 'Saved',
                });
            } catch (error) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    text: error,
                });
                console.log(error);
            }
        },
        async deleteUser(id) {
            try {
                await deleteEmployee(id);
                this.refetch();
                this.pushToast({
                    id: Date.now(),
                    type: 'success',
                    title: 'Deleted',
                });
            } catch (error) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    text: error,
                });
                console.log(error);
            }
        },
    },
};
</script>

<style lang="scss" src="./EmployeesPage.scss"></style>
